/**
 * BM Versioning MVI Store
 *
 * Architecture: MVI Pattern - Store (Pinia)
 * Connects intents, reducer, and effects.
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";

import type { TFeatureId } from "@/shared/types/feature";
import type { IBmVersioningState } from "./state";
import {
  createInitialState,
  selectCanCommit,
  selectCanCheckout,
  selectIsLoading,
  selectCurrentHead,
  selectArchivedCommits,
  selectCommitCount,
} from "./state";
import { reducer } from "./reducer";
import { versioningIntents, type TVersioningIntent } from "./intents";
import * as effects from "./effects";

export const useBmVersioningStore = defineStore("bm-versioning", () => {
  // ========================================================================
  // State
  // ========================================================================

  const state = ref<IBmVersioningState>(createInitialState());

  // ========================================================================
  // Dispatch
  // ========================================================================

  function dispatch(intent: TVersioningIntent): void {
    state.value = reducer(state.value, intent);
  }

  // ========================================================================
  // Computed (Selectors)
  // ========================================================================

  // Domain state
  const currentFeatureId = computed(() => state.value.domain.currentFeatureId);
  const history = computed(() => state.value.domain.history);
  const isDirty = computed(() => state.value.domain.isDirty);

  // UI state
  const isLoadingHistory = computed(() => state.value.ui.isLoadingHistory);
  const isCommitting = computed(() => state.value.ui.isCommitting);
  const isCheckingOut = computed(() => state.value.ui.isCheckingOut);
  const error = computed(() => state.value.ui.error);
  const isHistoryPanelOpen = computed(() => state.value.ui.isHistoryPanelOpen);
  const isCommitDialogOpen = computed(() => state.value.ui.isCommitDialogOpen);
  const checkoutTargetId = computed(() => state.value.ui.checkoutTargetId);

  // Derived selectors
  const canCommit = computed(() => selectCanCommit(state.value));
  const canCheckout = computed(() => selectCanCheckout(state.value));
  const isLoading = computed(() => selectIsLoading(state.value));
  const currentHead = computed(() => selectCurrentHead(state.value));
  const archivedCommits = computed(() => selectArchivedCommits(state.value));
  const commitCount = computed(() => selectCommitCount(state.value));

  // ========================================================================
  // Actions (Intent dispatchers + Effects)
  // ========================================================================

  /**
   * Set the current feature ID
   */
  function setCurrentFeature(featureId: TFeatureId | null): void {
    dispatch(versioningIntents.setCurrentFeature(featureId));
  }

  /**
   * Set dirty state
   */
  function setDirty(dirty: boolean): void {
    dispatch(versioningIntents.setDirty(dirty));
  }

  /**
   * Load commit history for the current feature
   * @returns false, если загрузка отклонена (причина — в `error`); без
   * выбранной фичи загружать нечего, и это не отказ
   */
  async function loadHistory(): Promise<boolean> {
    const featureId = state.value.domain.currentFeatureId;
    if (!featureId) return true;

    return effects.loadHistory(dispatch, featureId);
  }

  /**
   * Create a commit
   * @returns The new feature ID (new HEAD), or null on error
   */
  async function commit(message?: string): Promise<TFeatureId | null> {
    const featureId = state.value.domain.currentFeatureId;
    if (!featureId) return null;

    return effects.commit(dispatch, featureId, message);
  }

  /**
   * Checkout (create branch) from a commit
   * @returns The new feature ID (branch), or null on error
   */
  async function checkout(
    commitFeatureId: TFeatureId,
    branchName?: string
  ): Promise<TFeatureId | null> {
    return effects.checkout(dispatch, commitFeatureId, branchName);
  }

  /**
   * Toggle history panel visibility
   */
  function toggleHistoryPanel(): void {
    dispatch(versioningIntents.toggleHistoryPanel());
  }

  /**
   * Open commit dialog
   */
  function openCommitDialog(): void {
    dispatch(versioningIntents.openCommitDialog());
  }

  /**
   * Close commit dialog
   */
  function closeCommitDialog(): void {
    dispatch(versioningIntents.closeCommitDialog());
  }

  /**
   * Set checkout target (for confirmation dialog)
   */
  function setCheckoutTarget(targetFeatureId: TFeatureId | null): void {
    dispatch(versioningIntents.setCheckoutTarget(targetFeatureId));
  }

  /**
   * Clear error
   */
  function clearError(): void {
    dispatch(versioningIntents.clearError());
  }

  /**
   * Reset store to initial state
   */
  function reset(): void {
    state.value = createInitialState();
  }

  // ========================================================================
  // Return
  // ========================================================================

  return {
    // State (for testing)
    state,

    // Computed - Domain
    currentFeatureId,
    history,
    isDirty,

    // Computed - UI
    isLoadingHistory,
    isCommitting,
    isCheckingOut,
    error,
    isHistoryPanelOpen,
    isCommitDialogOpen,
    checkoutTargetId,

    // Computed - Derived
    canCommit,
    canCheckout,
    isLoading,
    currentHead,
    archivedCommits,
    commitCount,

    // Actions
    setCurrentFeature,
    setDirty,
    loadHistory,
    commit,
    checkout,
    toggleHistoryPanel,
    openCommitDialog,
    closeCommitDialog,
    setCheckoutTarget,
    clearError,
    reset,
  };
});
